import { PropsWithChildren } from "react";

import { DangerAlert } from "~/components/Alert";
import { Button, Text } from "~/ui";
import { getTrpcErrorMessage } from "~/utils/trpc-errors";

export type TrpcQueryGateProps = PropsWithChildren<{
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** When true, suppresses stale cached error UI while a background refetch runs. */
  isFetching?: boolean;
  /** When false after load settles, shows errorFallback (avoids an empty modal body). */
  contentReady?: boolean;
  onRetry?: () => void;
  loadingMessage?: string;
  errorTitle?: string;
  errorFallback?: string;
}>;

/**
 * Renders loading/error states for tRPC-backed forms instead of bare "Error" text.
 * Prefer `OrderActivityOrderQueryGate` when prefetching order data for action modals.
 */
export const TrpcQueryGate = ({
  isLoading,
  isError,
  isFetching = false,
  contentReady = true,
  error,
  onRetry,
  loadingMessage = "Loading…",
  errorTitle = "Could not load data",
  errorFallback = "Data could not be loaded. Check your connection and try again.",
  children,
}: TrpcQueryGateProps) => {
  const showLoading = isLoading || (isFetching && isError);
  const showError = isError && !isFetching;
  const showMissingContent = !showLoading && !showError && !contentReady;

  if (showLoading) {
    return <Text size="sm">{loadingMessage}</Text>;
  }

  if (showError || showMissingContent) {
    return (
      <DangerAlert title={errorTitle}>
        <Text size="sm">{showError ? getTrpcErrorMessage(error, errorFallback) : errorFallback}</Text>
        {onRetry != null && (
          <Button variant="light" size="xs" mt="sm" onClick={() => onRetry()}>
            Try again
          </Button>
        )}
      </DangerAlert>
    );
  }

  return <>{children}</>;
};
